import type { Block } from '@/kova/spec/types'

/**
 * Drops notes that are about the ROADMAP rather than about the data on screen.
 *
 * # The rule
 *
 * A screen says what is true of the figures it is showing. It does not say what
 * the product cannot do yet. That belongs in an issue.
 *
 * `lg-vs` was rendering four panels of prose and zero balances — an essay about
 * invariant 2, about the absence of a freeze control, and about fields the
 * endpoint does not return — in front of an operator who opened it to compare
 * our book against the institution's. Twenty-eight of these existed across
 * twenty modules (#120). The gaps they described are now #121 (controls this
 * console has not wired) and go-wallet#825 (fields four reads do not return),
 * which is where they can be scheduled instead of read.
 *
 * # Why this matters more than tidiness
 *
 * It trains operators to skip prose. These screens will eventually carry a
 * notice that genuinely changes how a figure should be read — a stale provider
 * snapshot, a partial read, a degraded reconciler. That notice sits in the same
 * grey box as three paragraphs about what the console cannot do, and it gets
 * skipped, because everything in that box has been skippable so far.
 *
 * Law 20 stops degraded reporting being read as an outage. This is the same
 * failure pointing the other way: a warning that is always present is a warning
 * nobody reads.
 *
 * # Why it filters here rather than at each of twenty call sites
 *
 * `readScreen` is the one point every loader's output passes through, so one
 * rule applies to all of them and a new loader cannot miss it. Removing the
 * generators module by module is #122 — mechanical, and safe to do a file at a
 * time with the suite green, which a single twenty-file regex pass was not:
 * several of these notes are the entire return value of a helper, so deleting
 * the literal leaves a function returning nothing.
 *
 * Filtering by kicker is a string match and that is a real weakness. It is
 * bounded by the test that fails when a note's BODY reads like a roadmap claim,
 * which catches a new panel under a new kicker.
 */

/**
 * Kickers whose subject is the roadmap.
 *
 * Each names a note that explains an absence rather than a figure. `Partial
 * read`, `Truncated`, `Empty page, unread depth` and the refusal notes are
 * deliberately NOT here: those are about the read that just happened, and an
 * operator acts on them.
 */
/**
 * The longest a note may be before it is an essay.
 *
 * The kicker allowlist this replaces was the wrong shape. It caught the panels
 * it was written against and missed a dozen others — `INVARIANT 11`,
 * `WHY THERE IS NO ATTEMPT HISTORY`, `DELIVERY`, `CONTROLS`, `EMPTY` — because
 * a list of names cannot anticipate the next name.
 *
 * Length is the property that actually distinguishes them. "3 rows could not be
 * read" is a fact about the data. Four sentences about what the schema cannot
 * express is a design document. Nothing an operator needs mid-shift takes more
 * than a line or two, and anything that does belongs in an issue.
 */
const MAX_NOTE = 220

/** Refusals stay whatever their length: they carry the decision_ref (law 13). */
function isRefusal(block: Block): boolean {
  return block.t === 'note' && typeof block.ref === 'string' && block.ref.includes('decision_ref')
}

function isEssay(block: Block): boolean {
  if (block.t !== 'note') return false
  if (isRefusal(block)) return false
  const body = typeof block.body === 'string' ? block.body : ''
  return body.length > MAX_NOTE
}

/**
 * The screen's blocks with essays removed and table footers trimmed.
 *
 * `foot2` is the right-hand line under a table and carried the same prose by
 * another route — "the operator who authored each version is stored as
 * created_by and is not returned by this read" sat under the payer catalog.
 * A table's footer says how many rows; the second line is dropped.
 */
export function withoutRoadmapNotes(blocks: readonly Block[]): readonly Block[] {
  let changed = false
  const out: Block[] = []
  for (const b of blocks) {
    if (isEssay(b)) { changed = true; continue }
    if (b.t === 'table' && typeof b.foot2 === 'string' && b.foot2.length > 0) {
      out.push({ ...b, foot2: '' }); changed = true; continue
    }
    out.push(b)
  }
  return changed ? out : blocks
}

/** Kept for the test that asserts no shipped note reads like a roadmap claim. */
export const ROADMAP_PHRASES: readonly string[] = [
  'is not wired',
  'are not wired',
  'none is called',
  'does not exist yet',
  'argues for it',
  'absent by decision',
  'has not been designed',
]
